// CACHE BUST v4.0 - Contemporary design with glass morphism and micro-interactions
import React, { useState, useEffect } from 'react';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver.jsx';
function Hero() {
  const [badgeRef, badgeVisible] = useIntersectionObserver({
    threshold: 0.2
  });
  const [headingRef, headingVisible] = useIntersectionObserver({
    threshold: 0.2
  });
  const [subtextRef, subtextVisible] = useIntersectionObserver({
    threshold: 0.2
  });
  const [ctaRef, ctaVisible] = useIntersectionObserver({
    threshold: 0.2
  });
  const [trustRef, trustVisible] = useIntersectionObserver({
    threshold: 0.2
  });
  const [parallaxOffset, setParallaxOffset] = useState(0);

  // Parallax effect on scroll
  useEffect(() => {
    const handleScroll = () => {
      const offset = window.pageYOffset;
      setParallaxOffset(offset * 0.5);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <section id="home" className="relative min-h-[600px] md:min-h-[700px] lg:min-h-[85vh] flex items-center overflow-hidden">
      {/* Background Image with Parallax */}
      <div className="absolute inset-0 z-0">
        <img src="https://images.squarespace-cdn.com/content/v1/5e7be0f4a4a1a06a00b2ccbf/fccc9623-36d4-4abb-aaf3-d2d3604d854f/unsplash-image-GN3V3OkWO7Q.jpg?format=1500w" alt="Peaceful nature background" className="w-full h-full object-cover transition-transform duration-100 ease-out" style={{
        transform: `translateY(${parallaxOffset}px)`
      }} />
        <div className="absolute inset-0 bg-gradient-to-b from-sage-900/70 via-sage-800/60 to-sage-900/75"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 lg:py-40">
        <div className="max-w-5xl mx-auto text-center">
          {/* Badge - Clean & Simple */}
          <div ref={badgeRef} className={`mb-8 transition-all duration-800 ${badgeVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{
          transitionDelay: '200ms'
        }}>
            <span className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm text-white text-sm font-medium tracking-wide rounded-full border border-white/20 uppercase">
              In-Person in Redwood City & Online Across California
            </span>
          </div>

          {/* Main Heading - H1 */}
          <h1 ref={headingRef} className={`text-5xl md:text-6xl lg:text-7xl font-serif font-light text-white mb-8 leading-tight transition-all duration-800 ${headingVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'}`} style={{
          transitionDelay: '400ms',
          textShadow: '0 2px 30px rgba(0, 0, 0, 0.4)',
          letterSpacing: '-0.02em'
        }}>Stop People-Pleasing<br className="hidden md:block" /> Without Abandoning Yourself</h1>
          
          {/* Subheading - Supporting line */}
          <p ref={subtextRef} className={`text-xl md:text-2xl lg:text-3xl text-white/90 font-light mb-12 max-w-3xl mx-auto transition-all duration-800 ${subtextVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{
          transitionDelay: '600ms',
          textShadow: '0 1px 15px rgba(0, 0, 0, 0.3)',
          lineHeight: '1.4'
        }}>
            In-person therapy in Redwood City and online throughout California.
          </p>

          {/* CTA Button - Single, Clear Action */}
          <div ref={ctaRef} className={`transition-all duration-800 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{
          transitionDelay: '800ms'
        }}>
            <a className="inline-block px-9 py-4 bg-warm-500 text-white rounded-full text-base font-semibold transition-all duration-300 hover:bg-warm-600 hover:scale-105 hover:shadow-2xl shadow-xl" href="https://calendly.com/yphilipos/30min" data-tracking-id="link-32924e5d78879a07">
              Book a Free Consultation
            </a>
          </div>

          {/* Trust Indicators - Minimal & Elegant */}
          <div ref={trustRef} className={`mt-16 flex flex-wrap justify-center gap-6 md:gap-10 transition-all duration-800 ${trustVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{
          transitionDelay: '1000ms'
        }}>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-warm-300" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-white/80 text-sm font-medium">Licensed MFT #105168</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-warm-300" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-white/80 text-sm font-medium">Trauma-Informed</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-warm-300" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-white/80 text-sm font-medium">Safe & Compassionate Space</span>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator - Subtle */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-bounce">
        <svg className="w-6 h-6 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
        </svg>
      </div>
    </section>;
}
export default Hero;